import React, { useEffect, useState } from "react";
import { Card, Typography, Box, Grid } from "@mui/material";
import axios from "axios";

import {
  AppWidgetSummary,
  AppCurrentVisits,
} from "../sections/@dashboard/app";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

export default function CompanyDashboard({ companyId }) {

  const [zones, setZones] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

  useEffect(() => {
    if (companyId) {
      getZones();
    }
  }, [companyId]);

  const getZones = async () => {
    try {
      const response = await axios.get(
        `${SERVER_URL}/api/zonas/by-company/${companyId}`
      );
      setZones(response.data);
      setLoaded(true);
    } catch (err) {
      console.log("error");
    }
  };

  // 🔥 STATISTIKA
  const companyStats = zones.reduce((acc, z) => {
    acc.total += z.numbertotal || 0;
    acc.active += z.numberaktivne || 0;
    acc.inactive += z.numberneaktivne || 0;
    acc.alarms += z.numberalarms || 0;
    acc.failed += z.numberfailed || 0;
    return acc;
  }, {
    total: 0,
    active: 0,
    inactive: 0,
    alarms: 0,
    failed: 0
  });

  // 🔥 CHART PODACI
  const companyCharts = zones.reduce((acc, z) => {
    acc.number45w += z.number45w || 0;
    acc.number60w += z.number60w || 0;
    acc.number90w += z.number90w || 0;

    acc.numberaktivne += z.numberaktivne || 0;
    acc.numberneaktivne += z.numberneaktivne || 0;

    acc.januar += z.januar || 0;
    acc.februar += z.februar || 0;
    acc.mart += z.mart || 0;
    acc.april += z.april || 0;
    acc.maj += z.maj || 0;
    acc.juni += z.juni || 0;
    acc.juli += z.juli || 0;
    acc.august += z.august || 0;
    acc.septembar += z.septembar || 0;
    acc.oktobar += z.oktobar || 0;
    acc.novembar += z.novembar || 0;
    acc.decembar += z.decembar || 0;

    return acc;
  }, {
    number45w: 0,
    number60w: 0,
    number90w: 0,
    numberaktivne: 0,
    numberneaktivne: 0,
    januar: 0,
    februar: 0,
    mart: 0,
    april: 0,
    maj: 0,
    juni: 0,
    juli: 0,
    august: 0,
    septembar: 0,
    oktobar: 0,
    novembar: 0,
    decembar: 0
  });

  if (!loaded) return null;

  return (
    <Card sx={{ backgroundColor: "#002855", color: "#ff9800", mb: 0 }}>
      <Typography sx={{ ml: 4, mt: 2, fontSize: '1.5rem', fontWeight: 600 }}>
        Zbirni pregled svih zona
      </Typography>

      <Box sx={{ p: 2 }}>

        {/* 🔥 RED 1: GRAF + WIDGETI */}
        <Grid container spacing={3}>

          {/* 🔹 LIJEVO – GRAF */}
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: "#001B44", color: "#ff9800", height: '100%' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Ukupna potrošnja po mjesecima
                </Typography>

                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={[
                        { name: 'Jan', value: companyCharts.januar },
                        { name: 'Feb', value: companyCharts.februar },
                        { name: 'Mar', value: companyCharts.mart },
                        { name: 'Apr', value: companyCharts.april },
                        { name: 'Maj', value: companyCharts.maj },
                        { name: 'Jun', value: companyCharts.juni },
                        { name: 'Jul', value: companyCharts.juli },
                        { name: 'Aug', value: companyCharts.august },
                        { name: 'Sep', value: companyCharts.septembar },
                        { name: 'Okt', value: companyCharts.oktobar },
                        { name: 'Nov', value: companyCharts.novembar },
                        { name: 'Dec', value: companyCharts.decembar },
                      ]}
                    >
                      <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                      <XAxis dataKey="name" stroke="#fff" />
                      <YAxis stroke="#fff" />
                      <Tooltip />
                      <Line type="monotone" dataKey="value" stroke="#ffeb3b" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Box>
            </Card>
          </Grid>

          {/* 🔹 DESNO – 6 WIDGETA */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Ukupno lampi"
                  total={companyStats.total}
                  icon={'mdi:lightbulb-group'}
                  sx={{
                    bgcolor: '#E3F2FD',
                    '& svg': { color: '#1565C0' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Uključeno lampi"
                  total={companyStats.active}
                  icon={'mdi:lightbulb-on'}
                  sx={{
                    bgcolor: '#FFF3C4',   // 🟡 blago, toplo žuto
                    '& svg': {
                      color: '#E6A800',   // zlatno-žuta (ne dere oči)
                      filter: 'drop-shadow(0 0 4px rgba(230,168,0,0.4))'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Isključeno lampi"
                  total={companyStats.inactive}
                  icon={'mdi:lightbulb-off'}
                  sx={{
                    bgcolor: '#F5F5F5',
                    '& svg': { color: '#757575' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Lampe pod alarmom"
                  total={companyStats.alarms}
                  icon={'mdi:alert-circle'}
                  sx={{
                    bgcolor: '#FFE0B2',          // 🟠 jači, topliji orange
                    '& svg': { color: '#E65100' } // tamniji orange za ikonu
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Lampe bez konekcije"
                  total={companyStats.failed}
                  icon={'mdi:signal-off'}
                  sx={{
                    bgcolor: '#FFEBEE',
                    '& svg': { color: '#C62828' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <AppWidgetSummary
                  title="Lampe sa kvarom"
                  total={companyStats.failed}
                  icon={'mdi:lightbulb-alert'}
                  sx={{
                    bgcolor: '#FFCDD2',
                    '& svg': { color: '#B71C1C' }
                  }}
                />
              </Grid>

            </Grid>
          </Grid>

        </Grid>

        {/* 🔥 RED 2: PIE CHARTOVI */}
        <Grid container spacing={3} sx={{ mt: 2 }}>

          <Grid item xs={12} sm={6} md={3}>
            <AppCurrentVisits
              title="Modeli lampi"
              chartData={[
                { label: 'S 45 W', value: companyCharts.number45w },
                { label: 'M 60 W', value: companyCharts.number60w },
                { label: 'L 90 W', value: companyCharts.number90w },
              ]}
              chartColors={['#FFF3C4', '#FFE082', '#FFB74D']}
              chartHeight={200}
              sx={{
                bgcolor: '#EAF3FF',          // 🔵 blago plavkasto
                '& .MuiCardHeader-root': { color: '#0D47A1' } // naslov da se uklopi
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppCurrentVisits
              title="Status lampi"
              chartData={[
                { label: 'Uključeno', value: companyCharts.numberaktivne },
                { label: 'Isključeno', value: companyCharts.numberneaktivne },
              ]}
              chartColors={['#FFD54F', '#BDBDBD']}   // 🟡 žuto + ⚪ sivo
              chartHeight={200}
              sx={{
                bgcolor: '#EAF3FF',          // 🔵 blago plavkasto
                '& .MuiCardHeader-root': { color: '#0D47A1' } // naslov da se uklopi
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppCurrentVisits
              title="Ukupna potrošnja po mjesecima "
              chartData={[
                { label: 'Januar', value: companyCharts.januar },
                { label: 'Februar', value: companyCharts.februar },
                { label: 'Mart', value: companyCharts.mart },
                { label: 'April', value: companyCharts.april },
                { label: 'Maj', value: companyCharts.maj },
                { label: 'Juni', value: companyCharts.juni },
                { label: 'Juli', value: companyCharts.juli },
                { label: 'August', value: companyCharts.august },
                { label: 'Septembar', value: companyCharts.septembar },
                { label: 'Oktobar', value: companyCharts.oktobar },
                { label: 'Novembar', value: companyCharts.novembar },
                { label: 'Decembar', value: companyCharts.decembar },
              ]}
              chartColors={[
                '#AEDFF7', // light blue
                '#B2EBF2', // cyan
                '#B2DFDB', // teal
                '#C8E6C9', // green
                '#DCEDC8', // lime
                '#FFF9C4', // yellow
                '#FFE0B2', // orange
                '#FFCCBC', // deep orange
                '#F8BBD0', // pink
                '#E1BEE7', // purple
                '#D1C4E9', // deep purple
                '#CFD8DC'  // blue grey
              ]}
              chartHeight={200}
              sx={{
                bgcolor: '#EAF3FF',          // 🔵 blago plavkasto
                '& .MuiCardHeader-root': { color: '#0D47A1' } // naslov da se uklopi
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppCurrentVisits
              title="Status alarma u zadnjih 30 dana "
              chartData={[
                { label: 'Riješeni', value: companyCharts.numberaktivne },
                { label: 'Neriješeni', value: companyCharts.numberneaktivne },
              ]}
              chartColors={['#A5D6A7', '#EF9A9A']}   // 🟢 soft green + 🔴 soft red
              chartHeight={200}
              sx={{
                bgcolor: '#EAF3FF',          // 🔵 blago plavkasto
                '& .MuiCardHeader-root': { color: '#0D47A1' } // naslov da se uklopi
              }}
            />
          </Grid>

        </Grid>

      </Box>
    </Card>
  );
}